import React, { createContext, useContext, useState } from "react";
import { api, getStoredUser, setStoredUser, clearStoredUser } from "./api";
import { microsoftLogin } from "./msal";

const AuthContext = createContext(null);
const ADMIN_ROLES = ["SuperAdmin", "Admin"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser);
  const loading = false;

  // Authorize an email against the crm UserMst: must be active + an admin role.
  const authorizeByEmail = async (email) => {
    const res = await api.get("/usermst");
    const match = (res.data || []).find(
      (u) => u.Email && u.Email.toLowerCase() === ("" + email).trim().toLowerCase() && u.isActive
    );
    if (!match) throw new Error(`No active account for ${email}`);
    if (!ADMIN_ROLES.includes(match.Role)) throw new Error("This account is not an admin");
    return match;
  };

  const finalize = (match) => {
    const safe = {
      apiid: match.apiid,
      Email: match.Email,
      Role: match.Role,
      FullName: match.FullName || `${match.FirstName || ""} ${match.LastName || ""}`.trim(),
    };
    setStoredUser(safe);
    setUser(safe);
    return safe;
  };

  // Email + password against the crm UserMst (same client-side pattern as the other sites).
  const login = async (email, password) => {
    const res = await api.get("/usermst");
    const match = (res.data || []).find(
      (u) =>
        u.Email &&
        u.Email.toLowerCase() === email.trim().toLowerCase() &&
        u.Password === password &&
        u.isActive
    );
    if (!match) throw new Error("Invalid email or password");
    if (!ADMIN_ROLES.includes(match.Role)) throw new Error("This account is not an admin");
    return finalize(match);
  };

  // Microsoft (Azure AD) sign-in: verify identity via MSAL, then authorize via UserMst.
  const loginWithMicrosoft = async () => {
    const account = await microsoftLogin();
    const email = account && (account.username || account.idTokenClaims?.email);
    if (!email) throw new Error("Microsoft sign-in returned no email");
    const match = await authorizeByEmail(email);
    return finalize(match);
  };

  const logout = () => { clearStoredUser(); setUser(null); };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithMicrosoft, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
